import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import Button from 'pages/Dashboard/components/Button';
import Controller from 'core/components/FormController';
import {
  useUploadBillingMedia,
  useUpsertEOB,
} from 'pages/Dashboard/pages/Billing/hooks';
import VisitBreakdownView from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/VisitBreakdownView';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import {
  paymentsEventChannel,
  EOBReviewEditAdjustmentsParams,
} from 'pages/Dashboard/services/eventChannels';
import {
  blobToDataUrl,
  getSubmitButtonText,
} from '@populateui/utils';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import {
  EOBForm,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/index';
import {
  WizardContext,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/wizardContext';
import {
  getInReviewInfoForEOB,
  serializeEOB,
} from 'pages/Dashboard/pages/Billing/Payments/utils';
import EOBPaymentInfo from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBPaymentInfo';
import PatientAdjustmentEditPopover from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/PatientAdjustmentEditPopover';

export function ReviewStepTitle() {
  return (
    <div className="text-sm text-gray-500 font-semibold">
      Review EOB Information
    </div>
  );
}

export default function EOBReviewStep() {
  const {
    onClose,
    setInReview,
    patients,
    eobExtractionJobId,
    adjustmentExceptionMap,
  } = useContext(WizardContext);
  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { isSubmitting },
  } = useFormContext();

  const [eobId, eobServiceCodePaymentsInfo, eobClaimPayments] = watch(['eob.eobId', 'eob.eobServiceCodePaymentInfos', 'eob.eobClaimPayments']);
  const isNew = isNil(eobId);

  const { mutateAsync: uploadBillingMedia } = useUploadBillingMedia();
  const { mutateAsync: upsertEOB } = useUpsertEOB();

  const onSubmit = async (data: EOBForm) => {
    try {
      const { eobDoc, ...payload } = data;
      const serializedPayload = serializeEOB(payload, adjustmentExceptionMap);
      const response = await upsertEOB({
        ...serializedPayload,
        eobExtractionJobId,
      });

      if (!isNil(eobDoc?.original)) {
        const dataUrl = await blobToDataUrl(
          eobDoc?.original ?? null,
        );
        await uploadBillingMedia({
          multimediaType: MultimediaType.EOB,
          fileName: eobDoc?.original?.name,
          dataUrl,
          originatorId: response?.eob?.eobId,
        });
      }
      onClose();
      paymentsEventChannel.emit('onPaymentsDataChanged', 'eobs');
    } catch (error) {
      console.error('EOB submit error', error);
    } finally {
      reset(data, { keepIsSubmitted: false, keepIsValid: true });
    }
  };

  const submitButtonText = useMemo(() => getSubmitButtonText(
    isNew,
    isSubmitting,
  ), [isNew, isSubmitting]);

  const data = useMemo(
    () => getInReviewInfoForEOB(eobServiceCodePaymentsInfo, patients, eobClaimPayments),
    [eobServiceCodePaymentsInfo, patients, eobClaimPayments],
  );

  const [editParams, setEditParams] = useState<EOBReviewEditAdjustmentsParams | null>(null);

  useEffect(() => {
    const unsubscribe = paymentsEventChannel.on('onEditEOBPatientAdjustments', setEditParams);
    return () => unsubscribe();
  }, []);

  return (
    <>
      {!isNil(editParams) && (
        <PatientAdjustmentEditPopover
          params={editParams}
          onClose={() => setEditParams(null)}
        />
      )}
      <div className="flex flex-1 flex-col w-full overflow-y-hidden">
        <div className="flex flex-1 gap-3 flex-col w-full overflow-y-auto">
          <EOBPaymentInfo readonly />
          <VisitBreakdownView
            data={data}
            isReview
            readonly
          />
        </div>
        <Controller
          control={control}
          name="eob.comment"
          render={({ field }) => (
            <TextAreaWithActions
              {...field}
              className="mt-3"
              placeholder="Add Comment"
              textAreaClassName="pb-8"
              rows={3}
              id="eob-comment"
              trackingLabel="EOB Comment"
            />
          )}
        />
      </div>
      <div className="flex justify-between w-full items-center gap-2">
        <Button
          variant="outlined"
          type="button"
          onClick={onClose}
        >
          Close
        </Button>
        <div className="flex justify-end gap-2 flex-1">
          <Button
            variant="outlined"
            onClick={() => { setInReview(false); }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {submitButtonText}
          </Button>
        </div>
      </div>
    </>
  );
}
